import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, InputBase, Typography } from '@mui/material';
import { searchArchiveExams } from '../mishtameretPortalApi';

/**
 * השלמת מבחנים קודמים — המבחנים שנסגרו בקו הטלפוני לא מוצגים ברשימה, רק דרך חיפוש
 * (מספר מבחן או שם מסכת) במסגרת הנוכחית.
 */
const ArchiveExamSearch = ({ theme, frameId, frameLabel, startingId, onStart }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setQuery('');
    setResults(null);
    setError('');
  }, [frameId]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const data = await searchArchiveExams(q, frameId);
        if (!cancelled) setResults(data.exams || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בחיפוש');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, frameId]);

  const actionLabel = (exam) => {
    if (exam.status === 'completed') return `נבחנתם · ציון ${exam.score ?? 0}`;
    if (exam.status === 'in_progress') return 'המשך מבחן';
    return 'השלמת מבחן';
  };

  return (
    <Box sx={{ mt: 4, pt: 2.5, borderTop: `1px solid ${theme.borderSoft}` }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>השלמת מבחנים קודמים</Typography>
      <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem', mt: 0.35, mb: 1.25 }}>
        מבחני {frameLabel} שכבר נסגרו בקו הטלפוני — חפשו לפי מספר מבחן או שם מסכת
      </Typography>

      <InputBase
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="למשל: 112 או מנחות"
        inputProps={{ 'aria-label': 'חיפוש מבחן קודם', inputMode: 'search' }}
        sx={{
          width: '100%',
          minHeight: 44,
          px: 1.75,
          borderRadius: 999,
          border: `1px solid ${theme.border}`,
          bgcolor: theme.surface,
          color: theme.text,
          '&.Mui-focused': { borderColor: theme.accent },
        }}
      />

      {error ? <Typography sx={{ color: theme.danger, mt: 1.5 }}>{error}</Typography> : null}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: theme.accent }} />
        </Box>
      ) : results && !results.length ? (
        <Typography sx={{ color: theme.textMuted, mt: 1.5, fontSize: '0.9rem' }}>
          לא נמצא מבחן קודם תואם
        </Typography>
      ) : results ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
          {results.map((exam) => (
            <Box
              key={exam.id}
              sx={{
                p: 1.5,
                borderRadius: `${theme.radius}px`,
                border: `1px solid ${theme.borderSoft}`,
                bgcolor: theme.surface,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { sm: 'center' },
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }}>{exam.title}</Typography>
                <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
                  {exam.materialLabel ? `${exam.materialLabel} · ` : ''}{exam.questionCount} שאלות
                </Typography>
              </Box>
              <Button
                disabled={exam.status === 'completed' || startingId === exam.id}
                onClick={() => onStart(exam.id)}
                sx={{
                  minHeight: 40,
                  px: 2,
                  borderRadius: 999,
                  border: `1px solid ${theme.accent}`,
                  color: theme.accent,
                  fontWeight: 700,
                  flexShrink: 0,
                  '&:hover': { bgcolor: theme.surfaceHover },
                  '&.Mui-disabled': { borderColor: theme.borderSoft, color: theme.textMuted },
                }}
              >
                {startingId === exam.id
                  ? <CircularProgress size={20} sx={{ color: theme.accent }} />
                  : actionLabel(exam)}
              </Button>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default ArchiveExamSearch;
